import * as React from 'react';
import { useSearchParams } from 'react-router-dom';
import { createPortal } from 'react-dom';
import styles from './PlayablesCatalog.module.css';
import { 
  playables, 
  inputables, 
  getPlayableById, 
  getInputableById,
  getFilteredPlayables,
  type Playable,
  type Inputable,
  type PlayableResult,
  type InputableResult
} from './catalog';

// Define props interface using imported types
interface PlayablesCatalogProps {
  onPlayableSelect: (playable: PlayableResult | null) => void;
  onInputableSelect: (inputable: InputableResult | null) => void;
  setShowPlayablesPopup: (show: boolean) => void;
  isVisible: boolean;
  characterDisplayName?: string;
}

const PlayablesCatalog: React.FC<PlayablesCatalogProps> = ({ 
  onPlayableSelect, 
  onInputableSelect, 
  setShowPlayablesPopup,
  isVisible,
  characterDisplayName = 'your character'
}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [isAnimatedIn, setIsAnimatedIn] = React.useState<boolean>(false);
  
  // Get filtered playables using the shared function
  const filteredPlayables = React.useMemo(() => {
    return getFilteredPlayables();
  }, []);
  
  // Animation effect when component mounts
  React.useEffect(() => {
    if (isVisible) {
      // Small delay to ensure DOM is ready for the animation
      const timer = setTimeout(() => {
        setIsAnimatedIn(true);
      }, 10);
      
      return () => {
        clearTimeout(timer);
      };
    }
  }, [isVisible]);
  
  // Handle close with animation
  const handleClose = (): void => {
    setIsAnimatedIn(false);
    // Wait for animation to complete before unmounting
    setTimeout(() => {
      setShowPlayablesPopup(false);
    }, 300);
  };
  
  const handlePlayableClick = (playable: Playable): void => {
    if (playable.component || playable.behaviorType === 'background') {
      // Update URL with playable ID while preserving other query params
      setSearchParams(params => {
        params.set('playable', playable.id);
        return params;
      });

      onPlayableSelect(getPlayableById(playable.id));
      handleClose();
    }
  };

  const handleInputableClick = (inputable: Inputable): void => {
    onInputableSelect(getInputableById(inputable.id));
    // Clear playable from URL params while preserving others
    setSearchParams(params => {
      params.delete('playable');
      return params;
    });
  
    handleClose();
  };

  if (!isVisible) return null;

  return createPortal(
    <div 
      className={`${styles.backdrop} ${isAnimatedIn ? styles.backdropVisible : ''}`}
      onClick={handleClose}
    >
      <div 
        className={`${styles.popupContainer} ${isAnimatedIn ? styles.visible : ''}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className={styles.header}>
          <button className={styles.closeButton} onClick={handleClose}>×</button>
        </div>
        
        <div className={styles.content}>
          {/* Tools Section - Horizontal Scrolling */}
          <div className={styles.toolsSection}>
            <h4 className={styles.sectionTitle}>Tools</h4>
            <div className={styles.toolsScrollContainer}>
              {inputables.map((inputable: Inputable) => (
                <div 
                  key={inputable.id}
                  className={styles.toolCard}
                  onClick={() => handleInputableClick(inputable)}
                >
                  <div className={styles.toolIconContainer}>
                    <img src={inputable.icon} alt="" />
                  </div>
                  <div className={styles.toolTitle}>{inputable.title}</div>
                </div>
              ))}
            </div>
          </div>
          
          <div className={styles.activitiesSection}>
            <h4 className={styles.sectionTitle}>Activities</h4>
            <div className={styles.promotedGames}>
              {filteredPlayables.map((playable: Playable) => (
                <div 
                  key={playable.id}
                  className={styles.promotedGame}
                  onClick={() => handlePlayableClick(playable)}
                >
                  <div className={styles.promotedGameBanner}>
                    <img src={playable.image} alt={playable.title} />
                  </div>
                  <div className={styles.promotedGameInfo}>
                    <div className={styles.promotedGameIcon}>
                      <img src={playable.icon || playable.image} alt="" />
                    </div>
                    <div className={styles.promotedGameDetails}>
                      <div className={styles.promotedGameTitle}>{playable.title}</div>
                      <div className={styles.promotedGameDescription}>
                        {playable.description 
                          ? playable.description.replace('{{char}}', characterDisplayName)
                          : `Play ${playable.title} with ${characterDisplayName}`}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default PlayablesCatalog; 